import { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { API } from '../config/api';
import { toast } from 'sonner';
import { VideoFilterPipeline } from '../components/VideoFilterPipeline';

const CallContext = createContext(null);
export const useCall = () => useContext(CallContext);

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

export function CallProvider({ children }) {
  const { user, token } = useAuth();
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const ringtoneRef = useRef(null);
  const callTimerRef = useRef(null);

  const [callState, setCallState] = useState('idle'); // idle, calling, ringing, connected, ended
  const [callType, setCallType] = useState(null); // voice, video
  const [callId, setCallId] = useState(null);
  const [remoteUser, setRemoteUser] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);

  // Permission state: null | 'requesting' | 'granted' | 'denied' | 'blocked'
  const [permissionStatus, setPermissionStatus] = useState(null);
  const [pendingCallType, setPendingCallType] = useState(null);
  const pendingCallArgsRef = useRef(null);

  // Video filter state
  const filterPipelineRef = useRef(null);
  const [activeFilter, setActiveFilterState] = useState('normal');
  const [activeBackground, setActiveBackgroundState] = useState(null);
  const [bgLoading, setBgLoading] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);

  // Connect WebSocket for signaling
  useEffect(() => {
    if (!token || !user) return;

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsBase = API.replace(/^https?/, wsProtocol.replace(':', ''));
    const wsUrl = `${wsBase}/ws/call/${token}`;

    let ws;
    let reconnectTimer;
    let pingTimer;

    const connect = () => {
      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        wsRef.current = ws;
        pingTimer = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleSignalingMessage(data);
        } catch {}
      };

      ws.onclose = () => {
        wsRef.current = null;
        clearInterval(pingTimer);
        reconnectTimer = setTimeout(connect, 5000);
      };

      ws.onerror = () => {};
    };

    connect();

    return () => {
      clearTimeout(reconnectTimer);
      clearInterval(pingTimer);
      if (ws) ws.close();
    };
  }, [token, user]);

  // Call duration timer
  useEffect(() => {
    if (callState === 'connected') {
      setCallDuration(0);
      callTimerRef.current = setInterval(() => {
        setCallDuration(d => d + 1);
      }, 1000);
    } else {
      clearInterval(callTimerRef.current);
    }
    return () => clearInterval(callTimerRef.current);
  }, [callState]);

  const sendSignal = useCallback((data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  const handleSignalingMessage = useCallback(async (data) => {
    switch (data.type) {
      case 'incoming-call':
        setIncomingCall({
          callId: data.call_id,
          callerId: data.caller_id,
          callerName: data.caller_name,
          callerAvatar: data.caller_avatar,
          callType: data.call_type,
          conversationId: data.conversation_id,
        });
        // Play ringtone
        try {
          ringtoneRef.current = new Audio('data:audio/wav;base64,UklGRl9vT19teleXRlZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQ==');
          ringtoneRef.current.loop = true;
          ringtoneRef.current.play().catch(() => {});
        } catch {}
        break;

      case 'offer':
        await handleIncomingOffer(data);
        break;

      case 'answer':
        if (pcRef.current) {
          try {
            await pcRef.current.setRemoteDescription(new RTCSessionDescription(data.answer));
          } catch (e) {
            console.warn('[Call] Error setting remote description:', e);
          }
        }
        break;

      case 'ice-candidate':
        if (pcRef.current && data.candidate) {
          try {
            await pcRef.current.addIceCandidate(new RTCIceCandidate(data.candidate));
          } catch {}
        }
        break;

      case 'call-accept':
        setCallState('connected');
        break;

      case 'call-end':
        endCallCleanup(data.reason || 'ended');
        break;

      case 'pong':
        break;

      default:
        break;
    }
  }, []);

  const handleIncomingOffer = async (data) => {
    if (!pcRef.current) return;
    try {
      await pcRef.current.setRemoteDescription(new RTCSessionDescription(data.offer));
      const answer = await pcRef.current.createAnswer();
      await pcRef.current.setLocalDescription(answer);
      sendSignal({
        type: 'answer',
        answer: answer,
        call_id: data.call_id,
        target_user_id: data.caller_id,
      });
    } catch (e) {
      console.error('[Call] Error handling offer:', e);
    }
  };

  const createPeerConnection = useCallback((targetUserId) => {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        sendSignal({
          type: 'ice-candidate',
          candidate: event.candidate,
          call_id: callId,
          target_user_id: targetUserId,
        });
      }
    };

    pc.ontrack = (event) => {
      const stream = event.streams[0];
      remoteStreamRef.current = stream;
      setRemoteStream(stream);
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
        endCallCleanup('connection_lost');
      }
    };

    pcRef.current = pc;
    return pc;
  }, [sendSignal, callId]);

  // Check permission status using Permissions API (where supported)
  const checkPermissionStatus = async (type) => {
    try {
      if (navigator.permissions && navigator.permissions.query) {
        const micResult = await navigator.permissions.query({ name: 'microphone' }).catch(() => null);
        const camResult = type === 'video'
          ? await navigator.permissions.query({ name: 'camera' }).catch(() => null)
          : null;

        const micState = micResult?.state || 'prompt';
        const camState = camResult?.state || 'prompt';

        // If either is denied and we can't re-prompt, it's blocked
        if (micState === 'denied' || (type === 'video' && camState === 'denied')) {
          return 'blocked';
        }
        if (micState === 'granted' && (type !== 'video' || camState === 'granted')) {
          return 'granted';
        }
        return 'prompt';
      }
    } catch {
      // Permissions API not available (Safari iOS) — fall through
    }
    return 'unknown';
  };

  const getMediaStream = async (type) => {
    const constraints = {
      audio: true,
      video: type === 'video' ? { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } } : false,
    };

    console.log('[Permission] Initial request, type:', type);
    setPendingCallType(type);

    try {
      console.log('[Permission] Calling getUserMedia...');
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('[Permission] getUserMedia SUCCESS');
      localStreamRef.current = stream;
      setLocalStream(stream);
      setPermissionStatus('granted');
      return stream;
    } catch (err) {
      console.log('[Permission] getUserMedia FAILED:', err.name, err.message);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        // Check if it's truly blocked (browser remembered deny) vs user just denied the prompt
        const status = await checkPermissionStatus(type);
        console.log('[Permission] Post-failure status:', status);
        if (status === 'blocked') {
          setPermissionStatus('blocked');
        } else {
          setPermissionStatus('denied');
        }
      } else if (err.name === 'NotFoundError') {
        toast.error(`No ${type === 'video' ? 'camera or microphone' : 'microphone'} found on this device`);
        setPermissionStatus('denied');
      } else if (err.name === 'NotReadableError' || err.name === 'AbortError') {
        toast.error('Media device is already in use or unavailable');
        setPermissionStatus('denied');
      } else {
        toast.error('Failed to access media devices');
        setPermissionStatus('denied');
      }
      return null;
    }
  };

  // Retry permission request — now receives stream directly from PermissionGate
  // PermissionGate calls getUserMedia in its click handler (Safari-safe)
  // and passes the result here
  const retryPermission = useCallback(async (stream, err) => {
    const type = pendingCallType || 'voice';

    // If PermissionGate passed a stream, permission was granted
    if (stream) {
      console.log('[Permission] Stream received from PermissionGate — permission granted');
      localStreamRef.current = stream;
      setLocalStream(stream);
      setPermissionStatus('granted');

      // Resume pending call
      if (pendingCallArgsRef.current) {
        const args = pendingCallArgsRef.current;
        pendingCallArgsRef.current = null;
        if (args._acceptMode) {
          _completeAcceptCall(stream, {
            incCallId: args.callId,
            callerId: args.callerId,
            incType: args.callType,
            convId: args.conversationId,
          });
        } else {
          _continueStartCall(stream, args);
        }
      }
      return stream;
    }

    // If PermissionGate passed an error, handle it
    if (err) {
      console.log('[Permission] Error from PermissionGate:', err.name, err.message);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        const status = await checkPermissionStatus(type);
        console.log('[Permission] Post-failure status:', status);
        setPermissionStatus(status === 'blocked' ? 'blocked' : 'denied');
      } else if (err.name === 'NotFoundError') {
        toast.error(`No ${type === 'video' ? 'camera or microphone' : 'microphone'} found on this device`);
        setPermissionStatus('denied');
      } else {
        toast.error('Still unable to access media devices');
        setPermissionStatus('denied');
      }
      return null;
    }

    // Fallback: if called without args (shouldn't happen with new flow)
    // Call getUserMedia directly
    console.log('[Permission] Fallback: calling getUserMedia directly');
    const constraints = {
      audio: true,
      video: type === 'video' ? { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } } : false,
    };
    try {
      const s = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('[Permission] Fallback getUserMedia SUCCESS');
      localStreamRef.current = s;
      setLocalStream(s);
      setPermissionStatus('granted');

      if (pendingCallArgsRef.current) {
        const args = pendingCallArgsRef.current;
        pendingCallArgsRef.current = null;
        if (args._acceptMode) {
          _completeAcceptCall(s, {
            incCallId: args.callId,
            callerId: args.callerId,
            incType: args.callType,
            convId: args.conversationId,
          });
        } else {
          _continueStartCall(s, args);
        }
      }
      return s;
    } catch (e) {
      console.log('[Permission] Fallback getUserMedia FAILED:', e.name);
      const status = await checkPermissionStatus(type);
      setPermissionStatus(status === 'blocked' ? 'blocked' : 'denied');
      return null;
    }
  }, [pendingCallType]);

  // Cancel permission flow
  const cancelPermission = useCallback(() => {
    setPermissionStatus(null);
    setPendingCallType(null);
    pendingCallArgsRef.current = null;
    setCallState('idle');
  }, []);

  // Internal: continue start call after permission granted
  const _continueStartCall = async (stream, { convId, recipientId, recipientName, recipientAvatar, type }) => {
    setCallState('calling');
    setCallType(type);
    setConversationId(convId);
    setRemoteUser({ userId: recipientId, name: recipientName, avatar: recipientAvatar });

    try {
      const res = await fetch(`${API}/calls/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          conversation_id: convId,
          recipient_user_id: recipientId,
          call_type: type,
        }),
      });
      const result = await res.json();

      if (!res.ok) {
        toast.error(result.detail || 'Failed to initiate call');
        cleanupMedia();
        setCallState('idle');
        return;
      }

      if (result.status === 'missed') {
        toast.info(`${recipientName} is not available right now`);
        cleanupMedia();
        setCallState('idle');
        return;
      }

      const newCallId = result.call_id;
      setCallId(newCallId);

      const pc = createPeerConnection(recipientId);

      // For video calls, set up the filter pipeline
      if (type === 'video') {
        await _setupFilterPipeline(stream, pc);
      } else {
        stream.getTracks().forEach(track => pc.addTrack(track, stream));
      }

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      sendSignal({
        type: 'offer',
        offer: offer,
        call_id: newCallId,
        target_user_id: recipientId,
        call_type: type,
      });

    } catch (err) {
      console.error('[Call] Error starting call:', err);
      toast.error('Failed to start call');
      cleanupMedia();
      setCallState('idle');
    }
  };

  // Start a call
  const startCall = useCallback(async (convId, recipientId, recipientName, recipientAvatar, type) => {
    if (callState !== 'idle') {
      toast.error('You are already in a call');
      return;
    }

    // Store call args for retry
    pendingCallArgsRef.current = { convId, recipientId, recipientName, recipientAvatar, type };

    // Get media — this triggers the browser permission popup
    const stream = await getMediaStream(type);
    if (!stream) {
      // Permission denied/blocked — PermissionGate UI will show
      return;
    }

    // Permission granted — continue with call
    pendingCallArgsRef.current = null;
    await _continueStartCall(stream, { convId, recipientId, recipientName, recipientAvatar, type });
  }, [callState, token, sendSignal, createPeerConnection]);

  // Accept incoming call
  const acceptCall = useCallback(async () => {
    if (!incomingCall) return;

    const { callId: incCallId, callerId, callType: incType, conversationId: convId } = incomingCall;

    // Stop ringtone
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current = null;
    }

    // Store pending call data for retry flow
    pendingCallArgsRef.current = {
      _acceptMode: true,
      callId: incCallId,
      callerId,
      callType: incType,
      conversationId: convId,
      callerName: incomingCall.callerName,
      callerAvatar: incomingCall.callerAvatar,
    };

    const stream = await getMediaStream(incType);
    if (!stream) {
      // PermissionGate will show; don't decline yet — user can retry
      return;
    }

    pendingCallArgsRef.current = null;
    _completeAcceptCall(stream, { incCallId, callerId, incType, convId });

  }, [incomingCall, sendSignal, createPeerConnection]);

  const _completeAcceptCall = async (stream, { incCallId, callerId, incType, convId }) => {
    setCallState('connected');
    setCallType(incType);
    setCallId(incCallId);
    setConversationId(convId);
    setRemoteUser({ userId: callerId, name: incomingCall?.callerName, avatar: incomingCall?.callerAvatar });
    setIncomingCall(null);

    const pc = createPeerConnection(callerId);

    // For video calls, set up the filter pipeline
    if (incType === 'video') {
      await _setupFilterPipeline(stream, pc);
    } else {
      stream.getTracks().forEach(track => pc.addTrack(track, stream));
    }

    sendSignal({
      type: 'call-accept',
      call_id: incCallId,
      target_user_id: callerId,
    });
  };

  // Decline incoming call
  const declineCall = useCallback(() => {
    if (!incomingCall) return;

    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current = null;
    }

    sendSignal({
      type: 'call-end',
      call_id: incomingCall.callId,
      target_user_id: incomingCall.callerId,
      reason: 'declined',
    });

    setIncomingCall(null);
  }, [incomingCall, sendSignal]);

  // End active call
  const endCall = useCallback(() => {
    const targetId = remoteUser?.userId;
    if (targetId && callId) {
      sendSignal({
        type: 'call-end',
        call_id: callId,
        target_user_id: targetId,
        reason: 'ended',
      });
    }
    endCallCleanup('ended');
  }, [remoteUser, callId, sendSignal]);

  const endCallCleanup = (reason) => {
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current = null;
    }
    cleanupMedia();
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    setCallState('idle');
    setCallType(null);
    setCallId(null);
    setRemoteUser(null);
    setConversationId(null);
    setIsMuted(false);
    setIsCameraOff(false);
    setCallDuration(0);
    setLocalStream(null);
    setRemoteStream(null);
    setIncomingCall(null);
  };

  const cleanupMedia = () => {
    // Stop filter pipeline
    if (filterPipelineRef.current) {
      filterPipelineRef.current.stop();
      filterPipelineRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    setActiveFilterState('normal');
    setActiveBackgroundState(null);
  };

  /**
   * Set up the video filter pipeline.
   * Creates a canvas-based pipeline that filters the raw camera stream
   * and adds the filtered track to the peer connection.
   */
  const _setupFilterPipeline = async (rawStream, pc) => {
    try {
      const pipeline = new VideoFilterPipeline();
      pipeline.setFilter(activeFilter);
      const filteredStream = await pipeline.start(rawStream);

      if (filteredStream) {
        filterPipelineRef.current = pipeline;
        // Add filtered video track + raw audio track to PC
        const filteredVideoTrack = filteredStream.getVideoTracks()[0];
        const audioTrack = rawStream.getAudioTracks()[0];
        if (filteredVideoTrack) pc.addTrack(filteredVideoTrack, filteredStream);
        if (audioTrack) pc.addTrack(audioTrack, rawStream);
        console.log('[Call] Filter pipeline connected to peer connection');
      } else {
        // Fallback: use raw stream
        rawStream.getTracks().forEach(track => pc.addTrack(track, rawStream));
        console.log('[Call] Filter pipeline failed, using raw stream');
      }
    } catch (err) {
      console.error('[Call] Filter pipeline error, using raw stream:', err);
      rawStream.getTracks().forEach(track => pc.addTrack(track, rawStream));
    }
  };

  /**
   * Change the active video filter during a call.
   * Updates the canvas pipeline and optionally replaces the track in the peer connection.
   */
  const setActiveFilter = useCallback((filterId) => {
    setActiveFilterState(filterId);
    if (filterPipelineRef.current) {
      filterPipelineRef.current.setFilter(filterId);
    }
  }, []);

  /**
   * Set background effect (blur, gradient, solid, or null to disable).
   */
  const setActiveBackground = useCallback(async (mode, value) => {
    setActiveBackgroundState(mode ? { mode, value } : null);
    if (filterPipelineRef.current) {
      setBgLoading(true);
      await filterPipelineRef.current.setBackground(mode, value);
      setBgLoading(false);
    }
  }, []);

  /**
   * Set a custom image as background.
   */
  const setCustomBackgroundImage = useCallback(async (imageUrl) => {
    if (filterPipelineRef.current) {
      setBgLoading(true);
      const success = await filterPipelineRef.current.setBackgroundImage(imageUrl);
      if (success) {
        setActiveBackgroundState({ mode: 'image', value: imageUrl });
      }
      setBgLoading(false);
      return success;
    }
    return false;
  }, []);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  }, []);

  // Toggle camera
  const toggleCamera = useCallback(() => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsCameraOff(!videoTrack.enabled);
      }
    }
  }, []);

  // Switch camera (front/back on mobile)
  const switchCamera = useCallback(async () => {
    if (!localStreamRef.current) return;
    const videoTrack = localStreamRef.current.getVideoTracks()[0];
    if (!videoTrack) return;

    const currentFacing = videoTrack.getSettings().facingMode;
    const newFacing = currentFacing === 'user' ? 'environment' : 'user';

    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: newFacing, width: { ideal: 640 }, height: { ideal: 480 } },
      });
      const newTrack = newStream.getVideoTracks()[0];

      // If filter pipeline is active, restart it with new camera
      if (filterPipelineRef.current) {
        filterPipelineRef.current.stop();
        const pipeline = new VideoFilterPipeline();
        pipeline.setFilter(activeFilter);
        const filteredStream = await pipeline.start(new MediaStream([newTrack]));
        if (filteredStream) {
          filterPipelineRef.current = pipeline;
          const filteredTrack = filteredStream.getVideoTracks()[0];
          const sender = pcRef.current?.getSenders().find(s => s.track?.kind === 'video');
          if (sender && filteredTrack) await sender.replaceTrack(filteredTrack);
        }
      } else {
        // Replace track in peer connection
        const sender = pcRef.current?.getSenders().find(s => s.track?.kind === 'video');
        if (sender) await sender.replaceTrack(newTrack);
      }

      videoTrack.stop();
      localStreamRef.current.removeTrack(videoTrack);
      localStreamRef.current.addTrack(newTrack);
      setLocalStream(new MediaStream(localStreamRef.current.getTracks()));
    } catch {
      toast.error('Failed to switch camera');
    }
  }, [activeFilter]);

  // Toggle speaker (uses setSinkId where supported)
  const toggleSpeaker = useCallback(() => {
    setIsSpeakerOn(prev => !prev);
    // Note: setSinkId is not universally supported, especially on mobile Safari
    // The toggle state is tracked for UI purposes
  }, []);

  const value = {
    callState,
    callType,
    callId,
    remoteUser,
    conversationId,
    isMuted,
    isCameraOff,
    isSpeakerOn,
    callDuration,
    localStream,
    remoteStream,
    incomingCall,
    permissionStatus,
    pendingCallType,
    activeFilter,
    startCall,
    acceptCall,
    declineCall,
    endCall,
    toggleMute,
    toggleCamera,
    switchCamera,
    toggleSpeaker,
    setActiveFilter,
    activeBackground,
    bgLoading,
    setActiveBackground,
    setCustomBackgroundImage,
    retryPermission,
    cancelPermission,
    filterPipelineRef,
  };

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}
